import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProfileStateType } from "../../../types/state/profile";
import { fetchPasswordSetupStatus, getEmployeeProfile, updateEmployeeProfile } from "./api";

const initialState: ProfileStateType = {
    isLoadingProfile: false,
    isUpdatingProfile: false,
    isLoadingPasswordSetupStatus: false,
    employeeProfile: undefined,
    passwordSetupStatus: undefined,
    error: undefined,
    successMessage: undefined,
};

const profileSlice = createSlice({
    name: "profile",
    initialState,
    reducers: {
        /**
         * Clear any error state
         */
        clearError: (state) => {
            state.error = undefined;
        },
        
        /**
         * Clear success message
         */
        clearSuccessMessage: (state) => {
            state.successMessage = undefined;
        },
        
        /**
         * Clear the employee profile data
         */
        clearProfile: (state) => {
            state.employeeProfile = undefined;
        },
        clearPasswordSetupStatus: (state) => {
            state.passwordSetupStatus = undefined;
        },
        
        /**
         * Set the employee profile manually (if needed)
         */
        setProfile: (state, action: PayloadAction<ProfileStateType['employeeProfile']>) => {
            state.employeeProfile = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Get employee profile
        builder
            .addCase(getEmployeeProfile.pending, (state) => {
                state.isLoadingProfile = true;
                state.error = undefined;
            })
            .addCase(getEmployeeProfile.fulfilled, (state, action) => {
                state.isLoadingProfile = false;
                state.employeeProfile = action.payload;
            })
            .addCase(getEmployeeProfile.rejected, (state, action) => {
                state.isLoadingProfile = false;
                state.error = action.error.message;
            });

        // Password setup status
        builder
            .addCase(fetchPasswordSetupStatus.pending, (state) => {
                state.isLoadingPasswordSetupStatus = true;
            })
            .addCase(fetchPasswordSetupStatus.fulfilled, (state, action) => {
                state.isLoadingPasswordSetupStatus = false;
                state.passwordSetupStatus = action.payload;
            })
            .addCase(fetchPasswordSetupStatus.rejected, (state) => {
                state.isLoadingPasswordSetupStatus = false;
                state.passwordSetupStatus = undefined;
            });

        // Update employee profile
        builder
            .addCase(updateEmployeeProfile.pending, (state) => {
                state.isUpdatingProfile = true;
                state.error = undefined;
                state.successMessage = undefined;
            })
            .addCase(updateEmployeeProfile.fulfilled, (state, action) => {
                state.isUpdatingProfile = false;
                state.successMessage = action.payload.message;
            })
            .addCase(updateEmployeeProfile.rejected, (state, action) => {
                state.isUpdatingProfile = false;
                state.error = action.error.message;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearProfile,
    clearPasswordSetupStatus,
    setProfile,
} = profileSlice.actions;

export { profileSlice };
export default profileSlice.reducer;
